import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaSignOutAlt, FaCheckCircle, FaFilePdf, FaEye, FaDownload, FaHotel, FaArrowRight, FaCheck, FaClipboardList } from 'react-icons/fa';
import { submissionAPI, registrationAPI, accommodationAPI } from '../../api';
import './Dashboard.css';
import './DashboardAccommodation.css';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isResubmitMode, setIsResubmitMode] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [accommodationStatus, setAccommodationStatus] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loggedInUser = localStorage.getItem('user');
    if (loggedInUser) {
      const user = JSON.parse(loggedInUser);
      setUser(user);
      
      // Fetch all submission history from database
      const fetchSubmissionHistory = async () => {
        const result = await submissionAPI.getSubmissionHistory(user.username);
        if (result.success && result.submissions && result.submissions.length > 0) {
          setSubmissionHistory(result.submissions);
          // Set latest as main status
          setSubmissionStatus({
            fileName: result.submissions[0].fileName,
            uniqueFileId: result.submissions[0].uniqueFileId,
            submittedAt: new Date(result.submissions[0].submissionDate).toLocaleString(),
            status: 'Submitted'
          });
        }
      };

      // Fetch registration/payment status
      const fetchRegistrationStatus = async () => {
        const result = await registrationAPI.getRegistration(user.username);
        if (result.success && result.registration) {
          setRegistrationStatus({
            registered: true,
            participantCategory: result.registration.participantCategory,
            registrationType: result.registration.registrationType,
            totalAmount: result.registration.totalAmount,
            registrationStatus: result.registration.registrationStatus,
            paymentProofFileName: result.registration.paymentProofFileName,
            paymentDate: result.registration.paymentDate,
            registrationDate: new Date(result.registration.registrationDate).toLocaleString()
          });
        }
      };

      // Fetch accommodation status
      const fetchAccommodationStatus = async () => {
        const result = await accommodationAPI.getAccommodation(user.username);
        if (result.success && result.accommodation) {
          setAccommodationStatus({
            accommodated: true,
            checkInDate: result.accommodation.checkInDate,
            checkOutDate: result.accommodation.checkOutDate,
            numberOfDays: result.accommodation.numberOfDays,
            accommodationAmount: result.accommodation.accommodationAmount,
            accommodationStatus: result.accommodation.accommodationStatus,
            paymentProofFileName: result.accommodation.paymentProofFileName,
            paymentDate: result.accommodation.paymentDate,
            accommodationDate: new Date(result.accommodation.accommodationDate).toLocaleString()
          });
        }
      };
      
      fetchSubmissionHistory();
      fetchRegistrationStatus();
      fetchAccommodationStatus();
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    // Show logout success notification
    alert('✓ Logged out successfully');
    
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is PDF
      if (file.type !== 'application/pdf') {
        setSubmissionMessage('❌ Error: Please select a PDF file only. Current file type: ' + (file.type || 'unknown'));
        return;
      }
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        setSubmissionMessage(`❌ Error: File size (${fileSizeMB}MB) exceeds 5MB limit. Please select a smaller file.`);
        return;
      }
      setSelectedFile(file);
      setSubmissionMessage('✓ File selected: ' + file.name);
    }
  };

  const handleFileInputClick = () => {
    document.getElementById('abstract-file').click();
  };

  const handleResubmitClick = () => {
    setIsResubmitMode(true);
    setSelectedFile(null);
    setSubmissionMessage('');
  };

  const handleCancelResubmit = () => {
    setIsResubmitMode(false);
    setSelectedFile(null);
    setSubmissionMessage('');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Check if file is PDF
      if (file.type !== 'application/pdf') {
        setSubmissionMessage('❌ Error: Please select a PDF file only. Current file type: ' + (file.type || 'unknown'));
        return;
      }
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        setSubmissionMessage(`❌ Error: File size (${fileSizeMB}MB) exceeds 5MB limit. Please select a smaller file.`);
        return;
      }
      
      setSelectedFile(file);
      setSubmissionMessage('✓ File selected: ' + file.name);
    }
  };

  const handleSubmitAbstract = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setSubmissionMessage('Please select a PDF file to upload.');
      return;
    }

    setIsSubmitting(true);
    setSubmissionMessage('');

    try {
      // Call backend API to submit abstract
      const result = await submissionAPI.submit(user.username, user.id, selectedFile);
      setIsSubmitting(false);

      if (result.success && result.submission) {
        const submissionData = {
          fileName: result.submission.fileName,
          uniqueFileId: result.submission.uniqueFileId,
          submittedAt: result.submission.submittedAt ? new Date(result.submission.submittedAt).toLocaleString() : new Date().toLocaleString(),
          status: 'Submitted'
        };

        setSubmissionStatus(submissionData);
        
        // Fetch updated submission history
        const historyResult = await submissionAPI.getSubmissionHistory(user.username);
        if (historyResult.success && historyResult.submissions) {
          setSubmissionHistory(historyResult.submissions);
        }
        
        setSelectedFile(null);
        
        const message = isResubmitMode 
          ? 'Your abstract has been resubmitted successfully! Your updated file will be reviewed by the committee.' 
          : 'Your abstract has been submitted successfully! You will receive a confirmation email shortly.';
        setSubmissionMessage(message);
        
        // Exit resubmit mode on success
        setIsResubmitMode(false);

        // Keep message visible for 7 seconds instead of 5
        setTimeout(() => {
          setSubmissionMessage('');
        }, 7000);
      } else {
        const errorMsg = result.message || 'Failed to submit abstract. Please try again.';
        setSubmissionMessage(errorMsg);
        console.error('Submission failed:', result);
      }
    } catch (err) {
      setIsSubmitting(false);
      const errorMsg = err.message || 'An error occurred while submitting your abstract. Please check your connection and try again.';
      setSubmissionMessage(errorMsg);
      console.error('Submission error:', err);
    }
  };

  // Scroll to section when activeSection changes - REMOVED (now using modal)
  // useEffect(() => { ... }, [activeSection]);

  if (!user) return null;

  return (
    <div className="dashboard-page">
      {/* Dashboard Header */}
      <header className="dashboard-navbar">
        <div className="navbar-container">
          <h2 className="navbar-brand">Conference Dashboard</h2>
          <div className="navbar-right">
            <div className="profile-menu">
              <button
                className="profile-btn"
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              >
                <FaUser /> {user.username || user.name}
              </button>
              {showProfileDropdown && (
                <div className="dropdown-menu">
                  <div className="dropdown-header">
                    <h4>{user.title} {user.lastName}</h4>
                    <p>{user.email}</p>
                  </div>
                </div>
              )}
            </div>
            <button
              className="dashboard-logout-btn"
              onClick={handleLogout}
              title="Logout"
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="dashboard-container">
        <div className="dashboard-welcome">
          <h1>Welcome, {user.title} {user.lastName}!</h1>
          <p className="subtitle">IIITDM Conference 2026</p>
        </div>

        {/* Quick Action Buttons */}
        <section className="quick-actions-section">
          <div className="quick-actions-container">
            <button 
              className={`quick-action-btn abstract-btn`}
              onClick={() => setActiveSection('abstract')}
            >
              <FaFilePdf />
              <span>Abstract Submission</span>
            </button>
            <button 
              className={`quick-action-btn accommodation-btn`}
              onClick={() => setActiveSection('accommodation')}
            >
              <FaHotel />
              <span>Accommodation</span>
            </button>
            <button 
              className={`quick-action-btn registration-btn`}
              onClick={() => setActiveSection('registration')}
            >
              <FaClipboardList />
              <span>Registration</span>
            </button>
          </div>
        </section>

        {/* User Profile Information */}
        <section className="profile-section">
          <h2 className="section-title">Your Profile</h2>
          <div className="profile-grid">
            <div className="profile-item">
              <label>Title</label>
              <p>{user.title}</p>
            </div>
            <div className="profile-item">
              <label>Name Initials</label>
              <p>{user.nameInitials}</p>
            </div>
            <div className="profile-item">
              <label>Username</label>
              <p>{user.username}</p>
            </div>
            <div className="profile-item">
              <label>Email</label>
              <p>{user.email}</p>
            </div>
            <div className="profile-item">
              <label>Phone</label>
              <p>{user.phone}</p>
            </div>
            <div className="profile-item">
              <label>Affiliation</label>
              <p>{user.affiliation}</p>
            </div>
            <div className="profile-item">
              <label>Registration Date</label>
              <p>{user.registrationDate}</p>
            </div>
          </div>
        </section>

        {/* Abstract Submission Section - HIDDEN */}
        {false && (
        <section className="abstract-section">
          <h2 className="section-title">Submit Extended Abstract</h2>
          <p className="section-subtitle">Please submit your extended abstract in PDF format (maximum one page)</p>

          {isResubmitMode && (
            <div className="resubmit-notice">
              <p>🔄 <strong>Resubmission Mode Active</strong></p>
              <p>You are about to resubmit your abstract. Select your updated PDF file below and click "Resubmit Abstract" to confirm. Your previous submission will be replaced with the new one.</p>
            </div>
          )}

          {submissionMessage && (
            <div className={`submission-message ${submissionStatus ? 'success' : 'error'}`}>
              {submissionStatus && <FaCheckCircle />}
              {submissionMessage}
            </div>
          )}

          <div className="abstract-form-container">
            {!submissionStatus?.status || isResubmitMode ? (
              <form onSubmit={handleSubmitAbstract} className="abstract-form">
                <div className="file-input-section">
                  <input
                    type="file"
                    id="abstract-file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    disabled={false}
                    className="hidden-input"
                  />
                  <div 
                    className={`file-input-label ${isDragActive ? 'drag-active' : ''}`}
                    onClick={handleFileInputClick}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <FaFilePdf className="pdf-icon" />
                    <span className="file-text">
                      {selectedFile 
                        ? selectedFile.name 
                        : 'Click to select PDF file or drag and drop'}
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="dashboard-btn dashboard-btn-primary submit-btn"
                  disabled={isSubmitting || !selectedFile}
                >
                  {isSubmitting ? 'Submitting...' : isResubmitMode ? 'Resubmit Abstract' : 'Submit Abstract'}
                </button>
              </form>
            ) : null}

            {submissionStatus?.status === 'Submitted' && (
              <div className="submission-success">
                <FaCheckCircle className="success-icon" />
                <h3>Abstract Submitted Successfully!</h3>
                <p>File: <strong>{submissionStatus.fileName}</strong></p>
                <p>Submitted at: <strong>{submissionStatus.submittedAt}</strong></p>
                <p className="note">You will receive a confirmation email shortly.</p>
                
                {!isResubmitMode ? (
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn-secondary resubmit-btn"
                    onClick={handleResubmitClick}
                  >
                    Resubmit Updated Abstract
                  </button>
                ) : (
                  <div className="resubmit-actions">
                    <button
                      type="button"
                      className="dashboard-btn dashboard-btn-secondary cancel-resubmit-btn"
                      onClick={handleCancelResubmit}
                    >
                      Cancel Resubmission
                    </button>
                  </div>
                )}

                {/* Submission History */}
                {submissionHistory.length > 0 && (
                  <div className="submission-history">
                    <h4>📋 Submission History ({submissionHistory.length})</h4>
                    <div className="history-list">
                      {submissionHistory.map((submission, index) => (
                        <div key={submission.id} className="history-item">
                          <div className="history-item-header">
                            <span className="history-badge">
                              {index === 0 ? '✨ Latest' : `Version ${submissionHistory.length - index}`}
                            </span>
                            <span className="history-date">
                              {new Date(submission.submissionDate).toLocaleString()}
                            </span>
                          </div>
                          <div className="history-item-content">
                            <p className="history-filename">
                              <FaFilePdf /> {submission.fileName}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Submission Guidelines */}
          <div className="guidelines">
            <h3>Submission Guidelines</h3>
            <ul>
              <li>Format: PDF only</li>
              <li>Maximum size: 5 MB</li>
              <li>Maximum length: 1 page</li>
              <li>Include title, authors, and abstract content</li>
              <li>Use 12pt font or larger</li>
            </ul>
          </div>
        </section>
        )}

        {/* Registration & Payment Status Section - HIDDEN */}
        {false && (
          <section className="payment-section">
            <h2 className="section-title">Registration & Payment Status</h2>
            <div className="registration-status-container">
              <div className="status-grid">
                <div className="status-item">
                  <label>Participant Category</label>
                  <p>{registrationStatus.participantCategory}</p>
                </div>
                <div className="status-item">
                  <label>Registration Type</label>
                  <p>{registrationStatus.registrationType}</p>
                </div>
                <div className="status-item">
                  <label>Total Amount</label>
                  <p>₹{registrationStatus.totalAmount}</p>
                </div>
                <div className="status-item">
                  <label>Payment Date</label>
                  <p>{registrationStatus.paymentDate ? new Date(registrationStatus.paymentDate).toLocaleDateString() : '-'}</p>
                </div>
                <div className="status-item">
                  <label>Registration Date</label>
                  <p>{registrationStatus.registrationDate}</p>
                </div>
              </div>

              {registrationStatus.paymentProofFileName && (
                <div className="payment-proof-section">
                  <h3>Payment Proof</h3>
                  <p className="payment-proof-info">File: <strong>{registrationStatus.paymentProofFileName}</strong></p>
                  <div className="payment-proof-actions">
                    <button
                      className="dashboard-btn dashboard-btn-secondary view-proof-btn"
                      onClick={() => registrationAPI.viewPaymentProof(user.username)}
                    >
                      👁️ View Proof
                    </button>
                    <button
                      className="dashboard-btn dashboard-btn-secondary download-proof-btn"
                      onClick={() => registrationAPI.downloadPaymentProof(user.username)}
                    >
                      ⬇️ Download Proof
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Accommodation & Payment Status Section - HIDDEN */}
        {false && (
          <section className="dashboard-accommodation-section">
            <h2 className="section-title">
              <FaHotel className="dashboard-accommodation-section-title-icon" />
              Hostel Accommodation Booking
            </h2>
            <p className="dashboard-accommodation-subtitle">Your accommodation booking details and payment information</p>
            <div className="dashboard-accommodation-status-container">
              <div className="dashboard-status-grid">
                <div className="dashboard-status-item">
                  <label>Check-In Date</label>
                  <p>{new Date(accommodationStatus.checkInDate).toLocaleDateString()}</p>
                </div>
                <div className="dashboard-status-item">
                  <label>Check-Out Date</label>
                  <p>{new Date(accommodationStatus.checkOutDate).toLocaleDateString()}</p>
                </div>
                <div className="dashboard-status-item">
                  <label>Number of Days</label>
                  <p>{accommodationStatus.numberOfDays} days</p>
                </div>
                <div className="dashboard-status-item">
                  <label>Total Amount (₹250/day)</label>
                  <p>₹{accommodationStatus.accommodationAmount}</p>
                </div>
                <div className="dashboard-status-item">
                  <label>Status</label>
                  <p>
                    <span className={`dashboard-status-badge dashboard-status-${accommodationStatus.accommodationStatus}`}>
                      {accommodationStatus.accommodationStatus?.toUpperCase()}
                    </span>
                  </p>
                </div>
                <div className="dashboard-status-item">
                  <label>Payment Date</label>
                  <p>{accommodationStatus.paymentDate ? new Date(accommodationStatus.paymentDate).toLocaleDateString() : '-'}</p>
                </div>
              </div>

              {accommodationStatus.paymentProofFileName && (
                <div className="dashboard-accommodation-proof-section">
                  <h3>Accommodation Payment Proof</h3>
                  <p className="dashboard-accommodation-proof-info">File: <strong>{accommodationStatus.paymentProofFileName}</strong></p>
                  <div className="dashboard-accommodation-proof-actions">
                    <button
                      className="dashboard-proof-btn dashboard-proof-btn-primary"
                      onClick={() => accommodationAPI.viewPaymentProof(user.username)}
                    >
                      <FaEye /> View Proof
                    </button>
                    <button
                      className="dashboard-proof-btn dashboard-proof-btn-secondary"
                      onClick={() => accommodationAPI.downloadPaymentProof(user.username)}
                    >
                      <FaDownload /> Download Proof
                    </button>
                  </div>
                </div>
              )}

              {/* Accommodation Bank Details */}
              <div className="dashboard-accommodation-bank-details">
                <h3>Hostel Accommodation Payment Details</h3>
                <p className="dashboard-accommodation-subtitle">Use these bank details exclusively for hostel accommodation payment</p>
                <div className="dashboard-bank-details-grid">
                  <div className="dashboard-bank-details-card">
                    <div className="dashboard-bank-detail-item">
                      <label>Bank Name</label>
                      <p>HDFC Bank</p>
                    </div>
                    <div className="dashboard-bank-detail-item">
                      <label>Account Name</label>
                      <p>Indian Institute of Information Technology Design and Manufacturing Kurnool-Hostel</p>
                    </div>
                    <div className="dashboard-bank-detail-item">
                      <label>Account Number</label>
                      <p>50100517285898</p>
                    </div>
                    <div className="dashboard-bank-detail-item">
                      <label>IFSC Code</label>
                      <p>HDFC0004159</p>
                    </div>
                    <div className="dashboard-bank-detail-item">
                      <label>Branch</label>
                      <p>C. Camp Center, Kurnool</p>
                    </div>
                  </div>
                  <div className="dashboard-qr-code-card">
                    <h4>Hostel Payment QR Code</h4>
                    <div className="dashboard-qr-code-container">
                      <img 
                        src="/hostel.png" 
                        alt="Hostel Payment QR Code" 
                        className="dashboard-qr-code-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<p style="text-align: center; color: #666; margin-top: 20px;">[QR Code for Hostel Payment]<br/><small>Scan to transfer hostel accommodation amount</small></p>';
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Accommodation Booking CTA Section - HIDDEN */}
        {false && (
          <section className="dashboard-accommodation-cta-section">
            <div className="dashboard-accommodation-cta-container">
              <h2 className="section-title">
                <FaHotel className="dashboard-accommodation-cta-icon" />
                On-Campus Hostel Accommodation
              </h2>
              <div className="dashboard-accommodation-cta-content">
                <h3>Book Your Stay</h3>
                <p>Arrange your on-campus hostel accommodation for the conference days. Comfortable lodging available at ₹250 per day.</p>
                <ul className="dashboard-accommodation-features">
                  <li><FaCheck /> On-campus hostel facility</li>
                  <li><FaCheck /> ₹250 per day</li>
                  <li><FaCheck /> Select your check-in and check-out dates</li>
                  <li><FaCheck /> Separate payment from registration</li>
                  <li><FaCheck /> Easy payment proof upload</li>
                </ul>
                <button 
                  className="dashboard-accommodation-cta-btn"
                  onClick={() => window.location.href = '/accommodation-booking'}
                >
                  <FaHotel /> Book Accommodation
                  <FaArrowRight />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Conference Registration Section - HIDDEN */}
        {false && (
        <section className="registration-cta-section">
          <h2 className="section-title">Conference Registration</h2>
          <div className="registration-cta-container">
            <div className="registration-cta-content">
              <h3>Register for the Conference</h3>
              <p>Complete your conference registration with automatic fee calculation based on registration date.</p>
              <ul className="registration-features">
                <li><FaCheck /> Choose your participant category</li>
                <li><FaCheck /> Fees automatically determined (Regular until 07 June 2026, 11:59 PM IST, Late after that)</li>
                <li><FaCheck /> Easy payment proof upload</li>
                <li><FaCheck /> Accommodation booking available separately</li>
              </ul>
              <button 
                className="dashboard-btn dashboard-btn-primary registration-cta-btn"
                onClick={() => window.location.href = '/conference-registration'}
              >
                <FaClipboardList /> Go to Registration
                <FaArrowRight />
              </button>
            </div>
          </div>
        </section>
        )}

        {/* Modal Overlay */}
        {activeSection && (
          <div className="dashboard-modal-overlay" onClick={() => setActiveSection(null)}>
            <div className="dashboard-modal-blob" onClick={(e) => e.stopPropagation()}>
              <button className="dashboard-modal-close" onClick={() => setActiveSection(null)}>
                ✕
              </button>

              {/* Abstract Submission Modal */}
              {activeSection === 'abstract' && (
                <section className="dashboard-modal-content">
                  <h2 className="section-title">Submit Extended Abstract</h2>
                  <p className="section-subtitle">Please submit your extended abstract in PDF format (maximum one page)</p>

                  {isResubmitMode && (
                    <div className="resubmit-notice">
                      <p>🔄 <strong>Resubmission Mode Active</strong></p>
                      <p>You are about to resubmit your abstract. Select your updated PDF file below and click "Resubmit Abstract" to confirm. Your previous submission will be replaced with the new one.</p>
                    </div>
                  )}

                  {submissionMessage && (
                    <div className={`submission-message ${submissionStatus ? 'success' : 'error'}`}>
                      {submissionStatus && <FaCheckCircle />}
                      {submissionMessage}
                    </div>
                  )}

                  <div className="abstract-form-container">
                    {!submissionStatus?.status || isResubmitMode ? (
                      <form onSubmit={handleSubmitAbstract} className="abstract-form">
                        <div className="file-input-section">
                          <input
                            type="file"
                            id="abstract-file"
                            accept=".pdf"
                            onChange={handleFileChange}
                            disabled={false}
                            className="hidden-input"
                          />
                          <div 
                            className={`file-input-label ${isDragActive ? 'drag-active' : ''}`}
                            onClick={handleFileInputClick}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                          >
                            <FaFilePdf className="pdf-icon" />
                            <span className="file-text">
                              {selectedFile 
                                ? selectedFile.name 
                                : 'Click to select PDF file or drag and drop'}
                            </span>
                          </div>
                        </div>

                        <button
                          type="submit"
                          className="dashboard-btn dashboard-btn-primary submit-btn"
                          disabled={isSubmitting || !selectedFile}
                        >
                          {isSubmitting ? 'Submitting...' : isResubmitMode ? 'Resubmit Abstract' : 'Submit Abstract'}
                        </button>
                      </form>
                    ) : null}

                    {submissionStatus?.status === 'Submitted' && (
                      <div className="submission-success">
                        <FaCheckCircle className="success-icon" />
                        <h3>Abstract Submitted Successfully!</h3>
                        <p>File: <strong>{submissionStatus.fileName}</strong></p>
                        <p>Submitted at: <strong>{submissionStatus.submittedAt}</strong></p>
                        <p className="note">You will receive a confirmation email shortly.</p>
                        
                        {!isResubmitMode ? (
                          <button
                            type="button"
                            className="dashboard-btn dashboard-btn-secondary resubmit-btn"
                            onClick={handleResubmitClick}
                          >
                            Resubmit Updated Abstract
                          </button>
                        ) : (
                          <div className="resubmit-actions">
                            <button
                              type="button"
                              className="dashboard-btn dashboard-btn-secondary cancel-resubmit-btn"
                              onClick={handleCancelResubmit}
                            >
                              Cancel Resubmission
                            </button>
                          </div>
                        )}

                        {/* Submission History */}
                        {submissionHistory.length > 0 && (
                          <div className="submission-history">
                            <h4>📋 Submission History ({submissionHistory.length})</h4>
                            <div className="history-list">
                              {submissionHistory.map((submission, index) => (
                                <div key={submission.id} className="history-item">
                                  <div className="history-item-header">
                                    <span className="history-badge">
                                      {index === 0 ? '✨ Latest' : `Version ${submissionHistory.length - index}`}
                                    </span>
                                    <span className="history-date">
                                      {new Date(submission.submissionDate).toLocaleString()}
                                    </span>
                                  </div>
                                  <div className="history-item-content">
                                    <p className="history-filename">
                                      <FaFilePdf /> {submission.fileName}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Submission Guidelines */}
                  <div className="guidelines">
                    <h3>Submission Guidelines</h3>
                    <ul>
                      <li>Format: PDF only</li>
                      <li>Maximum size: 5 MB</li>
                      <li>Maximum length: 1 page</li>
                      <li>Include title, authors, and abstract content</li>
                      <li>Use 12pt font or larger</li>
                    </ul>
                  </div>
                </section>
              )}

              {/* Registration Modal */}
              {activeSection === 'registration' && (
                <section className="dashboard-modal-content">
                  {registrationStatus?.registered ? (
                    <>
                      <h2 className="section-title">Registration & Payment Status</h2>
                      <div className="registration-status-container">
                        <div className="status-grid">
                          <div className="status-item">
                            <label>Participant Category</label>
                            <p>{registrationStatus.participantCategory}</p>
                          </div>
                          <div className="status-item">
                            <label>Registration Type</label>
                            <p>{registrationStatus.registrationType}</p>
                          </div>
                          <div className="status-item">
                            <label>Total Amount</label>
                            <p>₹{registrationStatus.totalAmount}</p>
                          </div>
                          <div className="status-item">
                            <label>Payment Date</label>
                            <p>{registrationStatus.paymentDate ? new Date(registrationStatus.paymentDate).toLocaleDateString() : '-'}</p>
                          </div>
                          <div className="status-item">
                            <label>Registration Date</label>
                            <p>{registrationStatus.registrationDate}</p>
                          </div>
                        </div>

                        {registrationStatus.paymentProofFileName && (
                          <div className="payment-proof-section">
                            <h3>Payment Proof</h3>
                            <p className="payment-proof-info">File: <strong>{registrationStatus.paymentProofFileName}</strong></p>
                            <div className="payment-proof-actions">
                              <button
                                className="dashboard-btn dashboard-btn-secondary view-proof-btn"
                                onClick={() => registrationAPI.viewPaymentProof(user.username)}
                              >
                                👁️ View Proof
                              </button>
                              <button
                                className="dashboard-btn dashboard-btn-secondary download-proof-btn"
                                onClick={() => registrationAPI.downloadPaymentProof(user.username)}
                              >
                                ⬇️ Download Proof
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="section-title">Conference Registration</h2>
                      <div className="registration-cta-container">
                        <div className="registration-cta-content">
                          <h3>Register for the Conference</h3>
                          <p>Complete your conference registration with automatic fee calculation based on registration date.</p>
                          <ul className="registration-features">
                            <li><FaCheck /> Choose your participant category</li>
                            <li><FaCheck /> Fees automatically determined (Regular until 07 June 2026, 11:59 PM IST, Late after that)</li>
                            <li><FaCheck /> Easy payment proof upload</li>
                            <li><FaCheck /> Accommodation booking available separately</li>
                          </ul>
                          <button 
                            className="dashboard-btn dashboard-btn-primary registration-cta-btn"
                            onClick={() => window.location.href = '/conference-registration'}
                          >
                            <FaClipboardList /> Go to Registration
                            <FaArrowRight />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </section>
              )}

              {/* Accommodation Modal */}
              {activeSection === 'accommodation' && (
                <section className="dashboard-modal-content">
                  {accommodationStatus?.accommodated ? (
                    <>
                      <h2 className="section-title">
                        <FaHotel className="dashboard-accommodation-section-title-icon" />
                        Hostel Accommodation Booking
                      </h2>
                      <p className="dashboard-accommodation-subtitle">Your accommodation booking details and payment information</p>
                      <div className="dashboard-accommodation-status-container">
                        <div className="dashboard-status-grid">
                          <div className="dashboard-status-item">
                            <label>Check-In Date</label>
                            <p>{new Date(accommodationStatus.checkInDate).toLocaleDateString()}</p>
                          </div>
                          <div className="dashboard-status-item">
                            <label>Check-Out Date</label>
                            <p>{new Date(accommodationStatus.checkOutDate).toLocaleDateString()}</p>
                          </div>
                          <div className="dashboard-status-item">
                            <label>Number of Days</label>
                            <p>{accommodationStatus.numberOfDays} days</p>
                          </div>
                          <div className="dashboard-status-item">
                            <label>Total Amount (₹250/day)</label>
                            <p>₹{accommodationStatus.accommodationAmount}</p>
                          </div>
                          <div className="dashboard-status-item">
                            <label>Payment Date</label>
                            <p>{accommodationStatus.paymentDate ? new Date(accommodationStatus.paymentDate).toLocaleDateString() : '-'}</p>
                          </div>
                        </div>

                        {accommodationStatus.paymentProofFileName && (
                          <div className="dashboard-accommodation-proof-section">
                            <h3>Accommodation Payment Proof</h3>
                            <p className="dashboard-accommodation-proof-info">File: <strong>{accommodationStatus.paymentProofFileName}</strong></p>
                            <div className="dashboard-accommodation-proof-actions">
                              <button
                                className="dashboard-proof-btn dashboard-proof-btn-primary"
                                onClick={() => accommodationAPI.viewPaymentProof(user.username)}
                              >
                                <FaEye /> View Proof
                              </button>
                              <button
                                className="dashboard-proof-btn dashboard-proof-btn-secondary"
                                onClick={() => accommodationAPI.downloadPaymentProof(user.username)}
                              >
                                <FaDownload /> Download Proof
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="dashboard-accommodation-cta-container">
                        <h2 className="section-title">
                          <FaHotel className="dashboard-accommodation-cta-icon" />
                          On-Campus Hostel Accommodation
                        </h2>
                        <div className="dashboard-accommodation-cta-content">
                          <h3>Book Your Stay</h3>
                          <p>Arrange your on-campus hostel accommodation for the conference days. Comfortable lodging available at ₹250 per day.</p>
                          <ul className="dashboard-accommodation-features">
                            <li><FaCheck /> On-campus hostel facility</li>
                            <li><FaCheck /> ₹250 per day</li>
                            <li><FaCheck /> Select your check-in and check-out dates</li>
                            <li><FaCheck /> Separate payment from registration</li>
                            <li><FaCheck /> Easy payment proof upload</li>
                          </ul>
                          <button 
                            className="dashboard-accommodation-cta-btn"
                            onClick={() => window.location.href = '/accommodation-booking'}
                          >
                            <FaHotel /> Book Accommodation
                            <FaArrowRight />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </section>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
